import { Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import config from '../../../../config';
import { legacyHashToReactRoute } from '../../../../Helpers/legacyHashToReactRoute';
import { BBBEEScorecardAtom } from '../../../../State/BBBEEScorecard.Atom';
import { BEEScorecardService } from '../../../Api/BEEScorecard/BEEScorecard';
import { MDBox } from '../MDBox';
import { MDTypography } from '../MDTypography';

interface TabConfig {
  label: string;
  route: string;
  iframeRoute?: string;
}

interface LegacyBEEPageProps {
  legacyRoute: string;
  additionalParams?: string;
  tabs?: TabConfig[];
  basePath?: string;
  elementName?: string;
  pageTitle?: string;
  hideHeaderInfo?: boolean;
  navigateTo?: (hash: string) => string | null;
}

interface RouteParams {
  BBBEEScorecardId: string;
}

interface LocationState {
  scorecardName?: string;
  scorecardTypeId?: number;
  isConsolidated?: boolean;
}

export const LegacyBEEPage = ({ legacyRoute, additionalParams = '', tabs, basePath, elementName, pageTitle = 'Data Page', hideHeaderInfo = false, navigateTo }: LegacyBEEPageProps) => {
  const { BBBEEScorecardId } = useParams<RouteParams>();
  const location = useLocation<LocationState>();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [BBBEEScorecard, setBBBEEScorecard] = useRecoilState(BBBEEScorecardAtom);
  const [activeTab, setActiveTab] = useState(0);
  const [iframeHeight, setIframeHeight] = useState('600px');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (BBBEEScorecardId) {
      setBBBEEScorecard(prev => ({
          ...prev,
          scorecardId: BBBEEScorecardId,
        scorecardName: location.state?.scorecardName || prev.scorecardName || '',
        scorecardTypeId: location.state?.scorecardTypeId || prev.scorecardTypeId,
        isConsolidated: location.state?.isConsolidated ?? prev.isConsolidated,
      }));
    }
  }, [BBBEEScorecardId, location.state, setBBBEEScorecard]);

  useEffect(() => {
    const fetchScorecardMeta = async () => {
      if (BBBEEScorecardId && (!BBBEEScorecard.scorecardTypeName || BBBEEScorecard.isConsolidated === undefined)) {
        try {
          const response = await BEEScorecardService.getScorecardMetaViewModel(BBBEEScorecardId);
          const meta = response.data;
          setBBBEEScorecard(prev => ({
            ...prev,
            scorecardName: meta.Name || prev.scorecardName,
            scorecardTypeId: meta.ScorecardTypeId || prev.scorecardTypeId,
            scorecardTypeName: meta.ScorecardTypeName,
            ReportPeriodDateFrom: meta.StartDate,
            ReportPeriodDateTo: meta.EndDate,
            isConsolidated: meta.IsConsolidated ?? prev.isConsolidated,
          }));
        } catch (error) {
          console.error(error);
        }
      }
    };
    fetchScorecardMeta();
  }, [BBBEEScorecardId, BBBEEScorecard.scorecardTypeName, BBBEEScorecard.isConsolidated, setBBBEEScorecard]);

  useEffect(() => {
    if (tabs && basePath) {
      const currentPath = location.pathname;
      const pathParts = currentPath.split('/');
      const lastPart = pathParts[pathParts.length - 1];
      const tabIndex = tabs.findIndex(tab => tab.route === lastPart);
      if (tabIndex !== -1) {
        setActiveTab(tabIndex);
      } else {
        setActiveTab(0);
      }
    } else {
      setActiveTab(0);
    }
  }, [location.pathname, tabs, basePath, elementName]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== config.publicUrl.replace(/\/$/, '')) {
        return;
      }

      if (event.data.type === 'RESIZE' && event.data.height) {
        setIframeHeight(`${event.data.height}px`);
        setIsLoading(false);
      }

      if (event.data.type === 'NAVIGATE' && event.data.hash) {
        const path = navigateTo?.(event.data.hash) ?? legacyHashToReactRoute(event.data.hash);
        if (path) history.push(path);
      }

      if (event.data.type === 'SCROLL_TO_TOP') {
        iframeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      if (event.data.type === 'SCORECARD_SAVED') {
        setBBBEEScorecard(prev => ({
          ...prev,
          scorecardId: event.data.scorecardId || prev.scorecardId,
          scorecardName: event.data.scorecardName ?? prev.scorecardName,
          scorecardTypeName: event.data.scorecardTypeName ?? prev.scorecardTypeName,
          ReportPeriodDateFrom: event.data.reportPeriodDateFrom ?? prev.ReportPeriodDateFrom,
          ReportPeriodDateTo: event.data.reportPeriodDateTo ?? prev.ReportPeriodDateTo,
        }));
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [navigateTo, history, setBBBEEScorecard]);

  const getCurrentRoute = () => {
    if (tabs && tabs.length > 0) {
      return tabs[activeTab].iframeRoute || tabs[activeTab].route;
    }
    return legacyRoute;
  };

  const buildIframeSrc = () => {
    const route = getCurrentRoute();

    const params = new URLSearchParams({
      hideNav: 'true',
      hideFooter: 'true',
      hideScorecardBar: 'true',
      hideDataPageTabs: 'true'
    });

    const queryString = `?${params.toString()}`;
    const fullAdditionalParams = additionalParams ? `${queryString}&${additionalParams}` : queryString;

    if (!BBBEEScorecardId) {
      return `${config.publicUrl}/#${route}${fullAdditionalParams}`;
    }

    if (route.includes('{ScorecardId}')) {
      const cleanRoute = route.replace('{ScorecardId}', BBBEEScorecardId).replace(/^[/#]+/, '');
      return `${config.publicUrl}/#${cleanRoute}${fullAdditionalParams}`;
    }

    const parts = route.split('/').filter(p => p);
    if (parts.length >= 2) {
      return `${config.publicUrl}/#${parts[0]}/${BBBEEScorecardId}/${parts.slice(1).join('/')}${fullAdditionalParams}`;
    }

    return `${config.publicUrl}/#${route}/${BBBEEScorecardId}${fullAdditionalParams}`;
  };

  const iframeSrc = buildIframeSrc();

  useEffect(() => {
    setIsLoading(true);
  }, [iframeSrc]);

  const measureIframeHeight = () => {
    try {
      if (iframeRef.current?.contentWindow) {
        const iframeDocument = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;

        if (iframeDocument) {
          const contentHeight = iframeDocument.documentElement.scrollHeight;

          if (contentHeight > 0) {
            setIframeHeight(`${contentHeight}px`);
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    setTimeout(() => {
      measureIframeHeight();
    }, 500);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (tabs && basePath && BBBEEScorecardId) {
      const actualPath = basePath.replace(':BBBEEScorecardId', BBBEEScorecardId);
      history.push({
        pathname: `${actualPath}/${tabs[newValue].route}`,
        state: location.state
      });
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '600px',
      }}
    >
      {!hideHeaderInfo && (
        <MDBox style={{ padding: "10px" }}>
            <MDTypography variant='h4' fontWeight='bold' style={{ paddingBottom: "15px" }}>
                {pageTitle}{elementName ? ` - ${elementName}` : ''}
            </MDTypography>
        </MDBox>
      )}
      {(config.environment === 'local' || config.environment === 'dev') && (
        <MDBox style={{ padding: "10px" }}>
          <Typography variant="caption" style={{ fontFamily: 'monospace', fontSize: '11px'}}>
            Route: {iframeSrc}
          </Typography>
        </MDBox>
      )} 
      {tabs && tabs.length > 0 && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: '#fafafa' }}
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      )}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <iframe
          key={iframeSrc}
          ref={iframeRef}
          src={iframeSrc}
          title={`Legacy BEE Page - ${getCurrentRoute()}`}
          style={{
            width: '100%',
            height: iframeHeight,
            minHeight: '600px',
            border: 'none',
            display: 'block',
            opacity: isLoading ? 0 : 1,
            overflow: 'hidden',
          }}
          onLoad={handleIframeLoad}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-top-navigation-by-user-activation"
          allow="fullscreen"
        />
      </Box>
    </Box>
  );
};
